import { useEffect, useMemo } from "react";
import { ThemeProvider } from "styled-components";
import { theme } from "../theme/theme";
import { LEGACY_PALETTES } from "../theme/legacyPalettes";
import { applyThemeTokens, legacyShapeFromTokens } from "../theme/tokens";
import { loadThemeFont } from "../theme/fonts";
import { getProjectConfig } from "../projects";
import { useConfig } from "./ConfigContext";

/**
 * Two theme layers, one owner:
 *
 * 1. TOKENS (src/theme/tokens.ts) — the six semantic colors + font, applied as
 *    CSS custom properties (--app-color-* and the --ion-color-* mappings).
 *    Source: the researcher-authored THEME study_config when present and valid
 *    (app.themeTokens, parsed in ConfigContext), otherwise the per-project
 *    default (src/projects/<slug>.ts).
 *
 * 2. LEGACY styled-components palette — when a researcher THEME exists it is
 *    bridged onto the legacy shape (legacyShapeFromTokens) so the ~40 styled
 *    consumers follow it too; otherwise resolved from the COLOR_THEME
 *    study_config exactly as before (unknown/missing key -> MENTALO). It
 *    disappears when studies migrate to token themes.
 */
const Theme = ({ children }) => {
  const { app } = useConfig();

  // Layer 1: apply the token theme (CSS variables, Ionic colors, font).
  // Re-runs when the study config lands so a researcher THEME wins over the
  // project default.
  useEffect(() => {
    const tokens = app?.themeTokens ?? getProjectConfig().theme;
    if (tokens) {
      applyThemeTokens(tokens);
      // Fire-and-forget: fetch the self-hosted @font-face rules for the chosen
      // fonts (no-op for System / Ciutadella Rounded / unset). Text renders
      // with the fallback stack until the woff2 lands (font-display: swap).
      loadThemeFont(tokens.font).catch(console.error);
      loadThemeFont(tokens.fontTitle).catch(console.error);
    }
  }, [app]);

  // Layer 2: legacy styled-components palette (unchanged resolution semantics
  // when no researcher THEME is set).
  const modifiedTheme = useMemo(() => {
    const appThemeKey = app?.theme ?? "MENTALO";
    const safe = app?.themeTokens
      ? legacyShapeFromTokens(app.themeTokens)
      : LEGACY_PALETTES[appThemeKey] ?? LEGACY_PALETTES["MENTALO"];

    return {
      ...theme,
      primary: {
        background: safe.primary.background ?? safe.primary.main,
        main: safe.primary.main,
        contrast: safe.primary.contrast,
      },
      secondary: {
        background: safe.secondary.background ?? safe.secondary.main,
        main: safe.secondary.main,
        contrast: safe.secondary.contrast,
      },
      svg: safe.svg,
    };
  }, [app]);

  return <ThemeProvider theme={modifiedTheme}>{children}</ThemeProvider>;
};

export default Theme;
